import { AttributeName } from "@keita/lanepatrol-sdk";
import {
  OsaNodeCoding,
  CreateOrUpdateOsaNodeCoding,
} from "../../coding/models/OsaNodeCoding";
import { OsaNode } from "../../osaway/models/OsaNode";
import { OsaWay } from "../../osaway/models/OsaWay";

export enum Col {
  coderName,
  codingDate,
  projectName,
  projectCity,
  routeName,
  photoDate,
  photoReference,
  photoName,
  distanceFromStart,
  distancetoNext,
  latitudeStart,
  longitudeStart,
  latitudeEnd,
  longitudeEnd,
  scoreGlobal,
  scoreVB,
  scoreBB,
  scoreBP,
  scoreSB,
  comment,
  areaType,
  facilityType,
  facilityAccess,
  looseOrSlipperySurface,
  tramOrTrainRails,
  majorSurfaceDeformationOrDrainOpening,
  fixedObstacleOnFacility,
  nonFixedObstacleOnFacility,
  lineOfSight,
  delineation,
  lightSegregation,
  facilityWidthPerDirection,
  flowDirection,
  widthRestriction,
  roadLane01,
  vehicleParking01,
  severeHazard01,
  objectOrLevelChange01,
  sidewalk01,
  roadLane13,
  vehicleParking13,
  severeHazard13,
  objectOrLevelChange13,
  sidewalk13,
  grade,
  curvature,
  streetLighting,
  pedestrianCrossing,
  intersectionBicycleFacility,
  intersectionApproach,
  intersectionOrRoadCrossing,
  crossingFacility,
  numberOfLanesAdjacentRoad,
  numberOfLanesIntersectingRoad,
  propertyAccess,
  peakPedestrianFlowAlongOrAcrossFacility,
  peakBicycleAndLVTrafficFlow,
  observedProportionOfCargoBikesAndMopeds,
  bicycleAndLVSpeedAverage,
  bicycleAndLVSpeedDifferential,
  roadAADT,
  heavyVehicleFlow,
  roadSpeedLimit,
  roadOperatingSpeed,
}

export const expectedCsvHeader = [
  "Coder name",
  "Coding date",
  "Project name",
  "Project city",
  "Cycle lane name",
  "Image capture date",
  "Image reference",
  "Image name",
  "Distance",
  "Length",
  "Latitude start",
  "Longitude start",
  "Latitude end",
  "Longitude end",
  "Section score global",
  "Section score VB",
  "Section score BB",
  "Section score BP",
  "Section score SB ",
  "Comments",
  "Area type",
  "Facility type",
  "Facility access",
  "Loose or slippery surface",
  "Tram or train rails",
  "Major surface deformation or drain opening",
  "Fixed obstacle on facility",
  "Non fixed obstacle on facility",
  "Line of sight",
  "Delineation",
  "Light segregation",
  "Facility width per direction",
  "Flow direction",
  "Width restriction",
  "Road lane - 0-1m",
  "Vehicle parking - 0-1m",
  "Severe hazard - 0-1m",
  "Object or level change - 0-1m",
  "Sidewalk - 0-1m",
  "Road lane - 1-3m",
  "Vehicle parking - 1-3m",
  "Severe hazard - 1-3m",
  "Object or level change - 1-3m",
  "Sidewalk - 1-3m",
  "Grade",
  "Curvature",
  "Street lighting",
  "Pedestrian crossing",
  "Intersection bicycle facility",
  "Intersection approach",
  "Intersection or road crossing",
  "Crossing facility",
  "Number of lanes adjacent road",
  "Number of lanes intersecting road",
  "Property access",
  "Peak pedestrian flow along or across facility",
  "Peak bicycle and LV traffic flow",
  "Observed proportion of cargo bikes and mopeds",
  "Bicycle and LV speed average",
  "Bicycle and LV speed differential",
  "Road AADT",
  "Heavy vehicle flow",
  "Road speed limit",
  "Road operating speed",
];

export type CsvRowArray = string[];

export type OptionFieldConfig = {
  col: Col;
  attributeName: AttributeName;
  label: string;
};

export const attributeFields: OptionFieldConfig[] = [
  {
    col: Col.areaType,
    attributeName: AttributeName.AreaType,
    label: "Area type",
  },
  {
    col: Col.facilityType,
    attributeName: AttributeName.FacilityType,
    label: "Facility type",
  },
  {
    col: Col.facilityAccess,
    attributeName: AttributeName.FacilityAccess,
    label: "Facility access",
  },
  {
    col: Col.looseOrSlipperySurface,
    attributeName: AttributeName.LooseOrSlipperySurface,
    label: "Loose or slippery surface",
  },
  {
    col: Col.tramOrTrainRails,
    attributeName: AttributeName.TramOrTrainRails,
    label: "Tram or train rails",
  },
  {
    col: Col.majorSurfaceDeformationOrDrainOpening,
    attributeName: AttributeName.MajorSurfaceDeformationOrDrainOpening,
    label: "Major surface deformation or drain opening",
  },
  {
    col: Col.fixedObstacleOnFacility,
    attributeName: AttributeName.FixedObstacleOnFacility,
    label: "Fixed obstacle on facility",
  },
  {
    col: Col.nonFixedObstacleOnFacility,
    attributeName: AttributeName.NonFixedObstacleOnFacility,
    label: "Non fixed obstacle on facility",
  },
  {
    col: Col.lineOfSight,
    attributeName: AttributeName.LineOfSight,
    label: "Line of sight",
  },
  {
    col: Col.delineation,
    attributeName: AttributeName.Delineation,
    label: "Delineation",
  },
  {
    col: Col.lightSegregation,
    attributeName: AttributeName.LightSegregation,
    label: "Light segregation",
  },
  {
    col: Col.facilityWidthPerDirection,
    attributeName: AttributeName.FacilityWidthPerDirection,
    label: "Facility width per direction",
  },
  {
    col: Col.flowDirection,
    attributeName: AttributeName.FlowDirection,
    label: "Flow direction",
  },
  {
    col: Col.widthRestriction,
    attributeName: AttributeName.WidthRestriction,
    label: "Width restriction",
  },

  {
    col: Col.roadLane01,
    attributeName: AttributeName.AdjacentFeatures0to1mRoadLane,
    label: "Road lane - 0-1m",
  },
  {
    col: Col.vehicleParking01,
    attributeName: AttributeName.AdjacentFeatures0to1mVehicleParking,
    label: "Vehicle parking - 0-1m",
  },
  {
    col: Col.severeHazard01,
    attributeName: AttributeName.AdjacentFeatures0to1mSevereHazard,
    label: "Severe hazard - 0-1m",
  },
  {
    col: Col.objectOrLevelChange01,
    attributeName: AttributeName.AdjacentFeatures0to1mObjectOrLevelChange,
    label: "Object or level change - 0-1m",
  },
  {
    col: Col.sidewalk01,
    attributeName: AttributeName.AdjacentFeatures0to1mSidewalk,
    label: "Sidewalk - 0-1m",
  },

  {
    col: Col.roadLane13,
    attributeName: AttributeName.AdjacentFeatures1to3mRoadLane,
    label: "Road lane - 1-3m",
  },
  {
    col: Col.vehicleParking13,
    attributeName: AttributeName.AdjacentFeatures1to3mVehicleParking,
    label: "Vehicle parking - 1-3m",
  },
  {
    col: Col.severeHazard13,
    attributeName: AttributeName.AdjacentFeatures1to3mSevereHazard,
    label: "Severe hazard - 1-3m",
  },
  {
    col: Col.objectOrLevelChange13,
    attributeName: AttributeName.AdjacentFeatures1to3mObjectOrLevelChange,
    label: "Object or level change - 1-3m",
  },
  {
    col: Col.sidewalk13,
    attributeName: AttributeName.AdjacentFeatures1to3mSidewalk,
    label: "Sidewalk - 1-3m",
  },

  { col: Col.grade, attributeName: AttributeName.Grade, label: "Grade" },
  {
    col: Col.curvature,
    attributeName: AttributeName.Curvature,
    label: "Curvature",
  },
  {
    col: Col.streetLighting,
    attributeName: AttributeName.StreetLighting,
    label: "Street lighting",
  },
  {
    col: Col.pedestrianCrossing,
    attributeName: AttributeName.PedestrianCrossing,
    label: "Pedestrian crossing",
  },
  {
    col: Col.intersectionBicycleFacility,
    attributeName: AttributeName.IntersectionBicycleFacility,
    label: "Intersection bicycle facility",
  },
  {
    col: Col.intersectionApproach,
    attributeName: AttributeName.IntersectionApproach,
    label: "Intersection approach",
  },
  {
    col: Col.intersectionOrRoadCrossing,
    attributeName: AttributeName.IntersectionOrRoadCrossing,
    label: "Intersection or road crossing",
  },
  {
    col: Col.crossingFacility,
    attributeName: AttributeName.CrossingFacility,
    label: "Crossing facility",
  },
  {
    col: Col.numberOfLanesAdjacentRoad,
    attributeName: AttributeName.NumberOfLanesAdjacentRoad,
    label: "Number of lanes adjacent road",
  },
  {
    col: Col.numberOfLanesIntersectingRoad,
    attributeName: AttributeName.NumberOfLanesIntersectingRoad,
    label: "Number of lanes intersecting road",
  },
  {
    col: Col.propertyAccess,
    attributeName: AttributeName.PropertyAccess,
    label: "Property access",
  },
  {
    col: Col.peakPedestrianFlowAlongOrAcrossFacility,
    attributeName: AttributeName.PeakPedestrianFlowAlongOrAcrossFacility,
    label: "Peak pedestrian flow along or across facility",
  },
  {
    col: Col.peakBicycleAndLVTrafficFlow,
    attributeName: AttributeName.PeakBicycleAndLVTrafficFlow,
    label: "Peak bicycle and LV traffic flow",
  },
  {
    col: Col.observedProportionOfCargoBikesAndMopeds,
    attributeName: AttributeName.ObservedProportionOfCargoBikesAndMopeds,
    label: "Observed proportion of cargo bikes and mopeds",
  },
  {
    col: Col.bicycleAndLVSpeedAverage,
    attributeName: AttributeName.BicycleAndLVSpeedAverage,
    label: "Bicycle and LV speed average",
  },
  {
    col: Col.bicycleAndLVSpeedDifferential,
    attributeName: AttributeName.BicycleAndLVSpeedDifferential,
    label: "Bicycle and LV speed differential",
  },
  {
    col: Col.roadAADT,
    attributeName: AttributeName.RoadAADT,
    label: "Road AADT",
  },
  {
    col: Col.heavyVehicleFlow,
    attributeName: AttributeName.HeavyVehicleFlow,
    label: "Heavy vehicle flow",
  },
];

export interface ImportCodingCsvParams {
  file: File;
  selectedWay: OsaWay;
  nodes: OsaNode[];
  codingsByNodeId: Record<string, OsaNodeCoding>;
}

export interface ImportCodingCsvResult {
  entries: CreateOrUpdateOsaNodeCoding[];
  errors: string[];
}
